import RouteConstants from '@/features/router/RouteConstants';
import { EMAIL_CONNECTOR_NAME_KEYWORD } from '@/features/campaigns/EmailCampaign/constants';
import { isConnectorConnected } from '@/utils/connectors';
import { EmailCampaignGateCard } from '@/features/campaigns/EmailCampaign/components/EmailCampaignGateCard';
import { useEmailCampaignGate } from '@/features/campaigns/EmailCampaign/hooks/useEmailCampaignGate';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useModalContext } from '@/hooks/useModalContext';
import { useCompanyValues } from '@/providers';
import { useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { useEmailTemplates } from '../../hooks/useEmailTemplates';
import { EmailTemplate } from '../../types/emailTemplates';
import { DeleteEmailTemplateModal } from '../DeleteEmailTemplateModal';
import { EmailTemplateListContent } from './EmailTemplateListContent';
import { EmailTemplateListControls } from './EmailTemplateListControls';
import { EmailTemplateListEmptyState } from './EmailTemplateListEmptyState';
import { EmailTemplateListErrorState } from './EmailTemplateListErrorState';
import { EmailTemplateListHeader } from './EmailTemplateListHeader';
import { EmailTemplateListLoadingState } from './EmailTemplateListLoadingState';
import { EmailTemplateListStats } from './EmailTemplateListStats';
import { useEmailTemplateListFilters } from './useEmailTemplateListFilters';

export const EmailTemplateList: FC = () => {
  const history = useHistory();
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const { openModal } = useModalContext();
  const { gate, isLoading: isGateLoading } = useEmailCampaignGate();

  const { data: customConnectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );
  const isMailtrapConnected = isConnectorConnected(customConnectors, EMAIL_CONNECTOR_NAME_KEYWORD);

  const { templates, isLoading: isTemplatesLoading, isError, reload } = useEmailTemplates({ skip: !isMailtrapConnected });
  const { selectedFilter, setSelectedFilter, viewMode, setViewMode, searchValue, setSearchValue, filteredItems, templateStats, templateFilters } =
    useEmailTemplateListFilters(templates);

  const handleCreateTemplate = () => {
    history.push(RouteConstants.createEmailTemplate);
  };

  const handleRequestDeleteTemplate = (template: EmailTemplate) => {
    openModal({
      renderContent: DeleteEmailTemplateModal,
      renderContentProps: {
        template: { id: template.id, name: template.name },
      },
      dismissable: true,
      fullWidth: false,
      fullHeight: false,
    });
  };

  const handleEditTemplate = (templateId: string) => {
    const template = templates.find((item) => item.id === templateId);
    history.push({
      pathname: RouteConstants.emailTemplatesEditHub,
      state: {
        editSelection: {
          id: templateId,
          name: template?.name ?? '',
        },
      },
    });
  };

  const handleTemplatesHubEdit = (actionType: 'edit' | 'delete') => {
    if (actionType === 'edit') {
      history.push(RouteConstants.emailTemplatesEditHub);
    } else {
      history.push(RouteConstants.emailTemplatesDeleteHub);
    }
  };

  if (isGateLoading) {
    return <EmailTemplateListLoadingState />;
  }

  if (gate === 'no-audience') {
    return <EmailCampaignGateCard variant='no-audience' />;
  }

  if (!isConnectorsLoading && !isMailtrapConnected) {
    return <EmailCampaignGateCard variant='connect-mailtrap' />;
  }

  const isLoading = isConnectorsLoading || isTemplatesLoading;
  const showError = isMailtrapConnected && !isLoading && isError;

  return (
    <section className='space-y-5 bg-surface'>
      <EmailTemplateListHeader onCreate={handleCreateTemplate} isCreateDisabled={isConnectorsLoading} />

      {isLoading ? <EmailTemplateListLoadingState /> : null}

      {showError ? <EmailTemplateListErrorState onRetry={reload} /> : null}

      {!isLoading && !showError ? (
        <>
          <EmailTemplateListStats stats={templateStats} />

          {templates.length > 0 ? (
            <EmailTemplateListControls
              templateFilters={templateFilters}
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              filteredCount={filteredItems.length}
              totalCount={templates.length}
              onEditHub={() => handleTemplatesHubEdit('edit')}
              onDeleteHub={() => handleTemplatesHubEdit('delete')}
            />
          ) : null}

          {filteredItems.length > 0 ? (
            <EmailTemplateListContent viewMode={viewMode} items={filteredItems} onEdit={handleEditTemplate} onRequestDelete={handleRequestDeleteTemplate} />
          ) : (
            <EmailTemplateListEmptyState
              variant={templates.length === 0 ? 'global' : 'filtered'}
              onCreate={templates.length === 0 ? handleCreateTemplate : undefined}
            />
          )}
        </>
      ) : null}
    </section>
  );
};
